/**
 * Naming the moderator behind what happens in a meeting.
 *
 * With one host, "The host muted you" was enough. With several co-hosts it left
 * everyone guessing which of them it was — and some changes said nothing at
 * all: a Host Controls switch flipped, someone made a co-host or a presenter, a
 * tile pinned for everyone. Signaling now stamps every broadcast a moderator's
 * command causes with `by` (their peer id) and `byName`; the rules here decide
 * what each one says, and to whom. Pure, so they are tested without a channel.
 *
 * The moderator who acted is never told about their own action: the switch,
 * the roster or the stage already shows them what they did.
 */
import type { MeetingHostControls } from '../store/meeting-host-controls.store'
import {
  selfNotice,
  type ForcedAction,
  type SelfNotice,
  type SelfStanding,
} from './meeting-notices'
import { isShareOnlyPeer } from './share-only'

/** Who a moderator's command came from, as signaling stamped it. */
export type Actor = { id: string | null; name: string | null }

/** One toast: a translation key and its variables. `personal` shows it whatever the in-meeting switch says. */
export type ModeratorNotice = { key: string; vars?: Record<string, string>; personal: boolean }

/** The actor on a payload, or null from a server that does not stamp one (or a change nobody made). */
export function readActor(payload: unknown): Actor | null {
  const p = (payload ?? {}) as { by?: unknown; byName?: unknown }
  const id = typeof p.by === 'string' && p.by ? p.by : null
  const name = typeof p.byName === 'string' && p.byName.trim() ? p.byName.trim() : null
  return id || name ? { id, name } : null
}

/** The name to show: the server's, else the roster's for that id, else none. */
export function actorName(
  actor: Actor | null,
  nameOf: (peerId: string) => string | undefined,
): string | null {
  if (!actor) return null
  if (actor.name) return actor.name
  const fromRoster = actor.id ? nameOf(actor.id)?.trim() : undefined
  return fromRoster || null
}

/** Somebody did this, and it was not this client. */
export function actedByOther(actor: Actor | null, selfId: string | null): actor is Actor {
  return !!actor && !(actor.id && actor.id === selfId)
}

// ── Force commands: mute, stop video, stop screen share ──────────────────

const ON_YOU: Record<ForcedAction, string> = {
  mute: 'meetingScreen.alertMutedBy',
  stop_video: 'meetingScreen.alertCameraStoppedBy',
  stop_screenshare: 'meetingScreen.alertShareStoppedBy',
}

/** Only when the room did not say who: an older server. */
const ON_YOU_UNNAMED: Record<ForcedAction, string> = {
  mute: 'meetingScreen.alertMutedByHost',
  stop_video: 'meetingScreen.alertCameraStoppedByHost',
  stop_screenshare: 'meetingScreen.alertShareStoppedByHost',
}

const ON_EVERYONE: Record<ForcedAction, string> = {
  mute: 'meetingScreen.alertMutedEveryone',
  stop_video: 'meetingScreen.alertStoppedEveryonesVideo',
  stop_screenshare: 'meetingScreen.alertStoppedAllShares',
}

/** The host control whose switching off sends this command to the whole room. */
export const CONTROL_FOR_ACTION: Record<ForcedAction, keyof MeetingHostControls> = {
  mute: 'useMicrophone',
  stop_video: 'useVideo',
  stop_screenshare: 'shareScreen',
}

/** A host control someone switched, as this client heard it. */
export type ControlChange = { id: string | null; name: string; at: number }

/** How close behind a switch its stop command has to arrive to be part of it. */
export const CONTROL_STOP_WINDOW_MS = 5000

/**
 * Whether a room-wide stop is the tail of a host control that was just
 * switched off — and announced — by the same moderator.
 *
 * Switching Microphone access off also mutes everyone (MeetingScreen sends the
 * switch first, then the stop), and "Asha turned off microphones for
 * participants" already says why the mic went. Without this, one click raised
 * two toasts about the same thing.
 */
export function stopCoveredByControl(
  action: ForcedAction,
  actor: Actor | null,
  changes: Partial<Record<keyof MeetingHostControls, ControlChange>>,
  controls: Pick<MeetingHostControls, 'useMicrophone' | 'useVideo' | 'shareScreen'>,
  now: number,
): boolean {
  const key = CONTROL_FOR_ACTION[action] as 'useMicrophone' | 'useVideo' | 'shareScreen'
  const change = changes[key]
  if (!change || !actor?.id || change.id !== actor.id) return false
  return controls[key] === false && now - change.at <= CONTROL_STOP_WINDOW_MS
}

export type ForcedNoticeInput = {
  action: ForcedAction
  /** It turned something off on this client (`forcedActionChanged`). */
  changed: boolean
  /** Sent with no target: Mute all, Stop all videos, Stop all screens. */
  bulk: boolean
  viewerIsModerator: boolean
  /** Who sent it, or null when the room did not say. */
  name: string | null
  /** See `stopCoveredByControl`. */
  coveredByControl: boolean
}

/**
 * What a force command tells the person it reached.
 *
 *  - Aimed at you and it changed something: "Asha muted you".
 *  - Room-wide, to a host or co-host: "Asha muted everyone" — the other
 *    moderators need to know a co-host just silenced the room, whether or not
 *    their own mic was on (shown regardless of the switch only if it was).
 *  - Room-wide, to an attendee it changed nothing for: nothing, as before.
 *  - Part of a host control just announced: nothing — unless it turned a
 *    moderator's own mic or camera off, which a rule for participants does not
 *    explain.
 */
export function forcedNotice(i: ForcedNoticeInput): ModeratorNotice | null {
  if (i.bulk && i.coveredByControl && !(i.viewerIsModerator && i.changed)) return null
  if (i.bulk && i.viewerIsModerator && i.name) {
    return { key: ON_EVERYONE[i.action], vars: { name: i.name }, personal: i.changed }
  }
  if (!i.changed) return null
  return i.name
    ? { key: ON_YOU[i.action], vars: { name: i.name }, personal: true }
    : { key: ON_YOU_UNNAMED[i.action], personal: true }
}

// ── Host Controls ─────────────────────────────────────────────────────────

const HOST_CONTROL_KEYS: readonly (keyof MeetingHostControls)[] = [
  'allowChat',
  'sendChat',
  'privateChat',
  'sendFiles',
  'useEmojis',
  'allowAnnotation',
  'useMicrophone',
  'useVideo',
  'shareScreen',
]

/**
 * The controls in a `host:controls` payload, and nothing else: the broadcast
 * also carries who changed them, which is not a control and must not be
 * written into the store as one.
 */
export function hostControlsFromWire(payload: unknown): Partial<MeetingHostControls> {
  const p = (payload ?? {}) as Record<string, unknown>
  const out: Partial<MeetingHostControls> = {}
  for (const key of HOST_CONTROL_KEYS) {
    const value = p[key]
    if (typeof value === 'boolean') out[key] = value
  }
  return out
}

/** The controls that differ between two states the room sent. */
export function changedHostControls(
  before: Partial<MeetingHostControls> | null,
  after: Partial<MeetingHostControls>,
): (keyof MeetingHostControls)[] {
  if (!before) return []
  return HOST_CONTROL_KEYS.filter(
    (key) =>
      typeof before[key] === 'boolean' && typeof after[key] === 'boolean' && before[key] !== after[key],
  )
}

/** "Asha turned off microphones for participants" — the key for one switch, either way. */
export function hostControlNoticeKey(key: keyof MeetingHostControls, on: boolean): string {
  return `meetingScreen.hostControl${on ? 'On' : 'Off'}.${key}`
}

/**
 * What everyone else hears when a moderator flips Host Controls switches.
 *
 * Personal, so it shows with the in-meeting switch off too: for an attendee it
 * changes what they may do, and for another co-host it changes the meeting
 * they are running. Nothing without a name — the join push and
 * `host:controls:get` carry none, and the room's rules arriving is not news.
 */
export function hostControlNotices(
  before: Partial<MeetingHostControls> | null,
  after: Partial<MeetingHostControls>,
  name: string | null,
): ModeratorNotice[] {
  if (!name) return []
  return changedHostControls(before, after).map((key) => ({
    key: hostControlNoticeKey(key, after[key] === true),
    vars: { name },
    personal: true,
  }))
}

// ── Roles and presenter ───────────────────────────────────────────────────

/** The parts of the room's roles a notice compares. */
export type RoleSnapshot = {
  byPeer: Readonly<Record<string, SelfStanding['role']>>
  presenters: readonly string[]
}

const standingOf = (roles: RoleSnapshot, peerId: string): SelfStanding => {
  const role = roles.byPeer[peerId] ?? 'attendee'
  return { selfId: peerId, role, canPresent: role !== 'attendee' || roles.presenters.includes(peerId) }
}

const SELF_BY: Record<SelfNotice, string> = {
  'you-are-host': 'meetingScreen.alertYouAreHostBy',
  'you-are-cohost': 'meetingScreen.alertYouAreCohostBy',
  'no-longer-host': 'meetingScreen.alertNoLongerHostBy',
  'no-longer-cohost': 'meetingScreen.alertNoLongerCohostBy',
  'can-present': 'meetingScreen.alertCanPresentBy',
  'cannot-present': 'meetingScreen.alertCannotPresentBy',
}

/** "Asha made you a co-host" — the named form of a `selfNotice`. */
export function selfNoticeByKey(notice: SelfNotice): string {
  return SELF_BY[notice]
}

const OTHER_BY: Record<SelfNotice, string> = {
  'you-are-host': 'meetingScreen.alertMadeHost',
  'you-are-cohost': 'meetingScreen.alertMadeCohost',
  'no-longer-host': 'meetingScreen.alertRemovedHost',
  'no-longer-cohost': 'meetingScreen.alertRemovedCohost',
  'can-present': 'meetingScreen.alertAllowedPresent',
  'cannot-present': 'meetingScreen.alertRevokedPresent',
}

/**
 * What a host or co-host is told when another moderator changes someone
 * else's role or presenter grant: "Asha made Ravi a co-host".
 *
 * Only for people in the room (`present`): a roster the room sends lists
 * everyone's current role, and someone who left since the last one would
 * otherwise read as "removed". Never this client — told in its own words — nor
 * the actor, whose own role changes when they hand the meeting over ("made
 * Ravi the host" already says it). Screen-share pages are left out: their
 * presenter grant is the share request's answer, not a role.
 */
export function othersRoleNotices(
  before: RoleSnapshot,
  after: RoleSnapshot,
  opts: {
    selfId: string | null
    actorId: string | null
    name: string
    present: (peerId: string) => boolean
    nameOf: (peerId: string) => string
  },
): ModeratorNotice[] {
  const ids = new Set([
    ...Object.keys(before.byPeer),
    ...Object.keys(after.byPeer),
    ...before.presenters,
    ...after.presenters,
  ])
  const out: ModeratorNotice[] = []
  for (const id of ids) {
    if (id === opts.selfId || id === opts.actorId || isShareOnlyPeer(id) || !opts.present(id)) continue
    const notice = selfNotice(standingOf(before, id), standingOf(after, id))
    if (!notice) continue
    out.push({ key: OTHER_BY[notice], vars: { name: opts.name, target: opts.nameOf(id) }, personal: false })
  }
  return out
}
